import uniq from 'lodash/uniq';
import get from 'lodash/get';
import map from 'lodash/map';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';

import { VARIANTS } from '@jobcase/react_pattern_library/components/Notification';

import entity from './entity';
import profileActions from '../profiles/actions';
import featureEmployerActions from '../../features/employers/actions';
import featureGrantsActions from '../../features/grants/actions';
import { getProfileKey } from '../../authentication/selectors';
import { setNotification } from '../../notification/actions';
import { getCurrentEmployer } from '../../features/employers/selectors';
import SubOrgAPI from '../../../../utils/api/subOrganizationsAPI';
import GrantsAPI from '../../../../utils/api/grantsAPI';
import ProfilesAPI from '../../../../utils/api/profilesAPI';
import { selectAllGrants } from './selectors';
import { getEmployerById } from '../employers/selectors';

const TYPES = {
  ...entity.TYPES,
  GET_UPWARD_GRANT_REQUEST: `${entity.name}/GET_UPWARD_GRANTS_REQUEST`,
  GET_UPWARD_GRANT_ERROR: `${entity.name}/GET_UPWARD_GRANTS_ERROR`,
  GET_UPWARD_GRANT_SUCCESS: `${entity.name}/GET_UPWARD_GRANTS_SUCCESS`,
};

const OMITTED_GRANT_KEYS = [
  'id',
  'employer_id',
  'profile_id',
  'created_at',
  'updated_at',
];

const getAllGrants = (organizationId, options = {}, showLoader = true, shouldFetchUpwardGrants = false) => async (dispatch, getState) => {
  const state = getState();
  const loggedInProfileKey = getProfileKey(state);

  if (showLoader) dispatch({ type: TYPES.GET_ALL_REQUEST });
  dispatch(featureGrantsActions.setIsGrantsRefreshing(true));
  try {
    let fetchedGrants;
    let fetchedUsersWithUpwardGrants;
    const jfOrganizationId = getEmployerById(organizationId)(state)?.upward_job_feed_id;

    if (shouldFetchUpwardGrants) {
      [fetchedGrants, fetchedUsersWithUpwardGrants] = await Promise.all([
        entity.api.getEmployerGrants(organizationId, {
          headers: {
            'X-Profile-Key': get(options, 'profileKey'),
            'X-Check-Roles': true,
          },
        }).catch((e) => {
          // eslint-disable-next-line no-console
          console.error(e);
        }),
        GrantsAPI().getUsersWithUpwardGrants(jfOrganizationId)
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.error(e);
          }),
      ]);
    } else {
      fetchedGrants = await entity.api.getEmployerGrants(organizationId, {
        headers: {
          'X-Profile-Key': get(options, 'profileKey'),
          'X-Check-Roles': true,
        },
      });
    }

    let employerGrants = isArray(fetchedGrants) ? fetchedGrants : [];
    const usersWithUpwardGrants = isArray(fetchedUsersWithUpwardGrants) ? fetchedUsersWithUpwardGrants : [];

    if (!isEmpty(employerGrants) || !isEmpty(usersWithUpwardGrants)) {
      const upwardUsersProfileKeys = map(usersWithUpwardGrants, user => user?.profileKey);
      const employerGrantProfileKeys = map(employerGrants, user => user.profileId);
      const usersProfileKeys = uniq([...employerGrantProfileKeys, ...upwardUsersProfileKeys]);

      const requestPayload = {
        data: {
          organization_ids: [organizationId],
        },
      };
      // These enrichments are independent once the roster keys are known. Running them
      // together removes one full network round trip from the initial Users-page load.
      const [compactProfilesResponse, allGrantsForEmployerKey] = await Promise.all([
        dispatch(profileActions.getAllCompact({ data: usersProfileKeys })),
        entity.api.getUserGrantsByIndexedKeys(requestPayload, {
          headers: {
            'X-Check-Roles': true,
          },
        }),
      ]);
      const compactProfiles = compactProfilesResponse?.data || [];

      const transformGrants = grants => grants.content.reduce((acc, profile) => {
        const profileId = profile.profile_id;
        const grantMap = profile.grant_map;

        Object.entries(grantMap).forEach(([employerId, grantsByEmployer]) => {
          Object.values(grantsByEmployer).forEach((grants) => {
            grants.forEach((grant) => {
              if (!acc[profileId]) {
                acc[profileId] = [];
              }
              acc[profileId].push({
                employer_id: employerId,
                profile_id: profileId,
                role: grant.role,
                level: grant.level,
              });
            });
          });
        });

        return acc;
      }, {});

      const allGrantsByProfiles = transformGrants(allGrantsForEmployerKey);

      employerGrants = map(employerGrants, (grant) => {
        const profileKey = get(grant, 'profileId', '');
        const profileGrants = map(get(allGrantsByProfiles, `${profileKey}`, []), grant => omit(grant, OMITTED_GRANT_KEYS));

        if (profileKey === loggedInProfileKey) {
          dispatch(featureEmployerActions.setEmployerGrantsById(organizationId, profileGrants));
        }
        return { ...grant, grants: profileGrants };
      });

      const upwardUserProfileKeysToAdd = upwardUsersProfileKeys.filter(profileKey => !employerGrantProfileKeys.includes(profileKey));

      const upwardUsers = upwardUserProfileKeysToAdd.map((profileKey) => {
        const compactProfile = compactProfiles.find(profile => profile?.key === profileKey);
        const firstName = compactProfile?.first_name;
        const lastName = compactProfile?.last_name;
        const emailAddress = usersWithUpwardGrants.find(user => user?.profileKey === profileKey)?.email;

        return ({
          profileId: profileKey,
          firstName,
          lastName,
          emailAddress,
          employerId: organizationId,
          grants: [],
          // Upward-only users have no user_grants rows, so the grant-status query
          // has nothing to say about them - without this the card renders an empty
          // "Status:", which reads as a broken account.
          status: 'PARTNER',
          // Explicit marker for the repair action: a zero-grant row is NOT a reliable
          // signal (a module user whose grants were all revoked also has none).
          isUpwardOnlyUser: true,
        });
      });

      employerGrants = [...employerGrants, ...upwardUsers];
    }

    dispatch({ type: TYPES.GET_ALL_REQUEST_SUCCESS, payload: employerGrants });
    dispatch(featureGrantsActions.setIsGrantsRefreshing(false));
    return employerGrants;
  } catch (error) {
    dispatch({ type: TYPES.GET_ALL_REQUEST_ERROR, payload: error });
    dispatch(featureGrantsActions.setIsGrantsRefreshing(false));
    dispatch(setNotification('An error occurred while loading users', VARIANTS.INFO));
    throw error;
  }
};

const getOrganizationProfiles = options => async (dispatch, getState) => {
  try {
    const state = getState();
    const organizationId = getCurrentEmployer(state);
    const response = await SubOrgAPI().getOrganizationProfiles(organizationId, options);
    return response;
  } catch (error) {
    return null;
  }
};

const createOrganizationProfile = (data, isAllEmployer = false) => async (dispatch, getState) => {
  try {
    const state = getState();
    const organizationId = getCurrentEmployer(state);
    const { grants, ...rest } = data.data;
    let response;
    if (isAllEmployer) {
      response = await SubOrgAPI().createOrganizationProfile(organizationId, { data: rest });
      const profileId = response.profile_id;
      await GrantsAPI().updateGrant('*', {
        data: [{
          profileId,
          subOrganizationId: '',
          grants,
        }],
      });
    } else {
      response = await SubOrgAPI().createOrganizationProfile(organizationId, data);
    }
    return response;
  } catch (error) {
    return null;
  }
};

const editOrganizationProfile = data => async (dispatch, getState) => {
  try {
    const state = getState();
    const organizationId = getCurrentEmployer(state);
    const response = await SubOrgAPI().editOrganizationProfile(organizationId, data);
    return response;
  } catch (error) {
    return null;
  }
};


const getUpwardGrantsByProfile = profileKey => async (dispatch, getState) => {
  const allProfileGrants = selectAllGrants(getState());

  const grantProfileDataIndex = allProfileGrants?.findIndex(({ profileId }) => profileId === profileKey);
  let profileUpwardGrants = null;

  dispatch({ type: TYPES.GET_UPWARD_GRANT_REQUEST });

  try {
    const response = await GrantsAPI().getUpwardGrantsByProfile(profileKey);

    const newData = [...allProfileGrants];

    if (grantProfileDataIndex > -1) {
      newData[grantProfileDataIndex] = {
        ...newData[grantProfileDataIndex],
        upward: response,
      };
    }

    dispatch({
      type: TYPES.GET_UPWARD_GRANT_SUCCESS,
      payload: newData,
    });

    profileUpwardGrants = response;
  } catch (error) {
    dispatch({ type: TYPES.GET_UPWARD_GRANT_ERROR, payload: error });
    dispatch(setNotification('An error occurred while loading upward grants.', VARIANTS.INFO));
    throw error;
  }

  return profileUpwardGrants;
};

const updateUpwardGrants = (profileKey, data) => async (dispatch) => {
  try {
    await GrantsAPI().updateUpwardGrants(profileKey, data);
  } catch (error) {
    dispatch({ type: TYPES.GET_UPWARD_GRANT_ERROR, payload: error });
    // 4xx bodies from the grants backend carry a sanitized, admin-actionable reason
    // (e.g. the LUT-540 identity-conflict 409s); surface it instead of the blind
    // generic string. 5xx and shapeless errors keep the generic message.
    const errorCode = Number(error?.error_code);
    const reason = errorCode >= 400 && errorCode < 500 && typeof error?.message === 'string'
      ? error.message.trim()
      : '';
    dispatch(setNotification(
      reason
        ? `Could not update upward grants: ${reason}`
        : 'An error occurred while updating upward grants.',
      VARIANTS.INFO,
    ));
    throw error;
  }
};

/**
 * Internal-only (JEA) account diagnostics for one or more users of an organization
 * (LUT-545). Read-only; the server enforces the boundary - a non-JEA session is rejected
 * there regardless of what the UI shows. No notification on failure: the inspect panel
 * renders its own honest error state.
 */
const getAccountDiagnostics = (organizationId, profileIds) => async () => GrantsAPI()
  .getAccountDiagnostics(organizationId, profileIds);

export {
  TYPES,
  getAllGrants,
  getOrganizationProfiles,
  createOrganizationProfile,
  editOrganizationProfile,
  getUpwardGrantsByProfile,
  updateUpwardGrants,
};

/**
 * Re-runs provisioning for an existing member of the org (LUT-530). The backend create
 * endpoint is idempotent for existing org profiles and, for accounts that never
 * materialized a platform user (no name/avatar/status on this page, no deliverable
 * email), it mints the missing arrival and records the registration event that repairs
 * them. The existing job title is fetched first so the replay does not blank it.
 */
const repairUserProvisioning = ({ profileKey, email, isUpwardUser }) => async (dispatch, getState) => {
  const state = getState();
  const organizationId = getCurrentEmployer(state);

  // The create endpoint unconditionally applies job_title, so the repair MUST NOT
  // proceed unless the existing profile row was actually retrieved - a swallowed
  // lookup failure here would blank the user's job title. A legitimately null title
  // on a retrieved row is preserved as-is.
  let jobTitle;
  try {
    const response = await SubOrgAPI().getOrganizationProfiles(organizationId, { profile_ids: profileKey });
    const content = get(response, 'content');
    if (!isArray(content) || content.length === 0) {
      throw new Error('profile lookup returned no rows');
    }
    jobTitle = content[0].job_title;
  } catch (error) {
    dispatch(setNotification(
      'Could not load this account before repairing it - nothing was changed. Please try again.',
      VARIANTS.INFO,
    ));
    return null;
  }

  try {
    const response = await SubOrgAPI().createOrganizationProfile(organizationId, {
      data: {
        email,
        job_title: jobTitle,
        upward_user: isUpwardUser,
        grants: [],
      },
    });
    dispatch(setNotification(
      // User association inside the repair's create call is synchronous (Phase -1
      // mechanism record): once this succeeds, the reset is deliverable immediately.
      'Account repaired. You can send the password reset email now.',
      VARIANTS.INFO,
    ));
    return response;
  } catch (error) {
    dispatch(setNotification('Could not repair this account. Please try again.', VARIANTS.INFO));
    return null;
  }
};

/**
 * Triggers the same password-reset email the public jobcase.com recover page sends.
 * The endpoint always returns 204 (anti-enumeration), so this can only report that the
 * request was made - not that an email was actually deliverable.
 */
const requestPasswordResetEmail = email => async (dispatch) => {
  try {
    await ProfilesAPI().sendPasswordResetEmail(email);
    dispatch(setNotification(
      `Password reset requested for ${email}. If the account can receive email, it will arrive shortly.`,
      VARIANTS.INFO,
    ));
    return true;
  } catch (error) {
    dispatch(setNotification('Could not request the password reset. Please try again.', VARIANTS.INFO));
    return false;
  }
};

export default {
  TYPES,
  ...entity.getActions(),
  getAllGrants,
  getOrganizationProfiles,
  createOrganizationProfile,
  editOrganizationProfile,
  getUpwardGrantsByProfile,
  updateUpwardGrants,
  repairUserProvisioning,
  requestPasswordResetEmail,
  getAccountDiagnostics,
};
